import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, PriorityBadge, StageBadge } from '@/components/primitives';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, AlertOctagon } from 'lucide-react';
import dayjs from 'dayjs';

export default function CalendarPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('all');
  const [events, setEvents] = useState([]);
  const [ref, setRef] = useState(dayjs());
  const navigate = useNavigate();

  useEffect(() => { api.get('/companies').then((r) => setCompanies(r.data)); }, []);

  const load = async (date) => {
    const monthStart = date.startOf('month').startOf('week');
    const monthEnd = date.endOf('month').endOf('week');
    const params = new URLSearchParams({ start: monthStart.format('YYYY-MM-DD'), end: monthEnd.format('YYYY-MM-DD') });
    if (companyId !== 'all') params.set('company_id', companyId);
    const r = await api.get(`/calendar/events?${params.toString()}`);
    setEvents(r.data.events || []);
  };
  useEffect(() => { load(ref); }, [ref, companyId]);

  const byDate = useMemo(() => {
    const map = {};
    for (const e of events) { map[e.date] = map[e.date] || []; map[e.date].push(e); }
    return map;
  }, [events]);

  const days = useMemo(() => {
    const start = ref.startOf('month').startOf('week');
    const end = ref.endOf('month').endOf('week');
    const arr = [];
    let d = start;
    while (d.isBefore(end) || d.isSame(end, 'day')) {
      arr.push(d);
      d = d.add(1, 'day');
    }
    return arr;
  }, [ref]);

  const today = dayjs().format('YYYY-MM-DD');

  return (
    <>
      <PageHeader
        title="Calendar & Follow-ups"
        description="Every follow-up date and onboarding checkpoint on one canvas so nothing slips."
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="h-9 w-56" data-testid="calendar-company"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All companies</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        }
      >
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setRef(ref.subtract(1, 'month'))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-display font-semibold text-lg min-w-[10rem] text-center">{ref.format('MMMM YYYY')}</div>
          <Button size="sm" variant="outline" onClick={() => setRef(ref.add(1, 'month'))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setRef(dayjs())} data-testid="cal-today">Today</Button>
        </div>
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="grid grid-cols-7 border-b bg-muted/40">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
              <div key={d} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d, i) => {
              const key = d.format('YYYY-MM-DD');
              const items = byDate[key] || [];
              const inMonth = d.isSame(ref, 'month');
              const isToday = key === today;
              return (
                <div key={i} className={`min-h-[110px] p-2 border-b border-r ${inMonth ? 'bg-card' : 'bg-muted/30'} ${isToday ? 'bg-primary/5' : ''}`} data-testid={`cal-day-${key}`}>
                  <div className={`text-xs font-medium ${inMonth ? '' : 'text-muted-foreground'} ${isToday ? 'text-primary' : ''}`}>{d.format('D')}</div>
                  <div className="mt-1 space-y-1">
                    {items.slice(0, 3).map((e) => (
                      <button key={e.id} onClick={() => e.entity_type === 'candidate' ? navigate(`/companies/${e.company_id}/recruitment?open=${e.entity_id}`) : navigate(`/companies/${e.company_id}/onboarding?open=${e.entity_id}`)} className={`w-full text-left text-[11px] rounded px-1.5 py-1 truncate hover:opacity-80 flex items-center gap-1 ${e.overdue ? 'bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]' : (e.priority === 'p0' ? 'bg-[hsl(28_70%_94%)] text-[hsl(28_70%_30%)]' : 'bg-[hsl(174_50%_92%)] text-[hsl(174_78%_24%)]')}`} data-testid={`cal-event-${e.id}`}>
                        {e.overdue && <AlertOctagon className="h-3 w-3 shrink-0" />}
                        <span className="truncate">{e.next_action || 'follow-up'}: {e.title}</span>
                      </button>
                    ))}
                    {items.length > 3 && <p className="text-[10px] text-muted-foreground">+{items.length - 3} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <h3 className="font-display font-semibold mb-2">All events in view</h3>
          {events.length === 0 ? <p className="text-sm text-muted-foreground">No events in this range.</p> : (
            <div className="grid md:grid-cols-2 gap-2">
              {events.map((e) => (
                <Card key={e.id} className="p-3 cursor-pointer hover:bg-accent" onClick={() => e.entity_type === 'candidate' ? navigate(`/companies/${e.company_id}/recruitment?open=${e.entity_id}`) : navigate(`/companies/${e.company_id}/onboarding?open=${e.entity_id}`)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{e.title}</p>
                      <p className="text-xs text-muted-foreground">{e.subtitle} · {e.company_name}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs ${e.overdue ? 'text-[hsl(var(--danger))] font-medium' : 'text-muted-foreground'}`}>{dayjs(e.date).format('DD MMM')}</p>
                      <div className="flex items-center gap-1 mt-1 justify-end"><StageBadge stage={e.stage} /><PriorityBadge priority={e.priority} /></div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
